/**
 * Super Admin "view as" (docs/specs/staff-view-as.md) - owner: ui-shell. Used by the customer and partner portals:
 * - ViewAsRedeem: the `/view-as#code=` landing. Works signed out (it creates the session), removes the code from the
 *   address bar first, redeems it once, then reloads the portal home so the app starts on the new session.
 * - ViewAsBanner: always-visible notice while a view-as session is active, with "End" (ends it, closes the tab).
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { createTranslator, type Translate } from '@qm/i18n';
import { useApp, useOptionalApp } from '../lib/app-context';
import { endViewAs, loadViewAsStatus, redeemViewAs, viewAsCodeFromHash, VIEW_AS_INACTIVE, type ViewAsStatus } from '../lib/view-as';

export type RedeemState = 'pending' | 'invalid';

export interface RedeemWindow {
  location: { hash: string; pathname: string; search: string; assign(url: string): void };
  history: { replaceState(data: unknown, unused: string, url?: string): void };
}

/** Read + strip the code, redeem it, and on success reload the portal home. Returns the outcome. */
export async function runViewAsRedeem(win: RedeemWindow, redeem: (code: string | null) => Promise<'ok' | 'invalid'> = redeemViewAs): Promise<'ok' | 'invalid'> {
  const code = viewAsCodeFromHash(win.location.hash);
  if (win.location.hash) win.history.replaceState(null, '', win.location.pathname + win.location.search);
  const result = await redeem(code);
  if (result === 'ok') win.location.assign('/');
  return result;
}

/** `t` comes from the surface's own translator when given (partner kit), else from the app context. */
export function ViewAsRedeem({ surface, t: own }: { surface: 'customer' | 'partner'; t?: Translate }) {
  const app = useOptionalApp();
  const t = own ?? app?.t ?? createTranslator('en');
  const [state, setState] = useState<RedeemState>('pending');
  // the code is single-use: redeem once per page load (StrictMode runs effects twice in development)
  const started = useRef(false);
  useEffect(() => {
    if (started.current || typeof window === 'undefined') return;
    started.current = true;
    void runViewAsRedeem(window).then((r) => { if (r === 'invalid') setState('invalid'); });
  }, []);
  return <ViewAsRedeemView t={t} state={state} surface={surface} />;
}

export function ViewAsRedeemView({ t, state, surface }: { t: Translate; state: RedeemState; surface: 'customer' | 'partner' }) {
  return (
    <section className="qm-page" aria-labelledby="page-title" data-testid="view-as-page" data-surface={surface}>
      <Panel>
        <Heading level={1} id="page-title">{t('shell.viewAs.title')}</Heading>
        {state === 'invalid' ? (
          <>
            <Text role="alert" data-testid="view-as-invalid">{t('shell.viewAs.invalid')}</Text>
            <a href="/signin">{t('shell.viewAs.back')}</a>
          </>
        ) : <Text role="status" aria-busy="true">{t('shell.viewAs.opening')}</Text>}
      </Panel>
    </section>
  );
}

/** One status call per signed-in session (no polling). Errors and active:false render nothing. */
export function ViewAsBanner({ surface, initial }: { surface: 'customer' | 'partner'; initial?: ViewAsStatus }) {
  const { t, me } = useApp();
  const [status, setStatus] = useState<ViewAsStatus>(initial ?? VIEW_AS_INACTIVE);
  const [ending, setEnding] = useState(false);
  const signedIn = Boolean(me);
  useEffect(() => {
    if (!signedIn || initial) return;
    let live = true;
    void loadViewAsStatus().then((s) => { if (live) setStatus(s); });
    return () => { live = false; };
  }, [signedIn, initial]);
  if (!status.active) return null;
  const end = () => { setEnding(true); void endViewAs(); };
  return <ViewAsBannerView t={t} surface={surface} reason={status.reason} ending={ending} onEnd={end} />;
}

export function ViewAsBannerView({ t, surface, reason, ending, onEnd }: { t: Translate; surface: 'customer' | 'partner'; reason: string | null; ending?: boolean; onEnd: () => void }) {
  return (
    <div className="qm-viewas" role="status" data-testid="view-as-banner">
      <p className="qm-viewas__text">
        <b>{t(surface === 'partner' ? 'shell.viewAs.bannerPartner' : 'shell.viewAs.bannerCustomer')}</b>
        {reason ? <span data-testid="view-as-reason">{t('shell.viewAs.reason', { reason })}</span> : null}
      </p>
      <Button variant="primary" onClick={onEnd} disabled={ending} data-testid="view-as-end">{t('shell.viewAs.end')}</Button>
    </div>
  );
}
